import React, { ReactElement, useState } from 'react';
import { useSelector } from 'react-redux';

import ToolbarTool from '../../../ToolbarTool';
import Icon from '../../../../../../components/icons/Icon';
import SheetContainer, {
    CloseButton,
    Content,
    Header,
    Heading,
    Overlay,
    Sheet,
} from '../../../../../../mobile/structural/sheet';
import useSheet from '../../../../../../mobile/structural/sheet/hooks/useSheet';
import { SheetId } from '../../../../../../mobile/structural/sheet/sheetTypes';
import { canShowLabelsToolSelector } from '../labelToolSelectors';
import { ImMNElement } from '../../../../../../../common/elements/elementModelTypes';
import LabelsSheetContent from './LabelsSheetContent';
import { Label } from '../../../../../../../common/labels/labelTypes';
import LabelEditSheet from './LabelEditSheet';
import LabelColorSheet from './LabelColorSheet';
import { selectCurrentUserLabels } from '../../../../../../element/labels/labelSelectors';

type MobileLabelsToolProps = {
    currentUserId: string;
    gridSize: number;
    name: string;
    selectedElements: Immutable.List<ImMNElement>;
};

const MobileLabelsTool = (props: MobileLabelsToolProps): ReactElement | null => {
    const { currentUserId, gridSize, name } = props;

    const canShowLabelsTool = useSelector(canShowLabelsToolSelector);
    const sheetProps = useSheet(SheetId.LabelsTool);
    const editSheetProps = useSheet(SheetId.LabelEditTool);
    const colorSheetProps = useSheet(SheetId.LabelColorTool);
    const { isSheetOpen, dispatchOpenSheet } = sheetProps;
    const [currentlyEditingLabelId, setCurrentlyEditingLabelId] = useState<string | null>(null);
    const labelMap = useSelector(selectCurrentUserLabels);
    const currentlyEditingLabel = labelMap?.[currentlyEditingLabelId || ''] || null;

    if (!canShowLabelsTool) return null;

    const handleClickEdit = (label: Label) => {
        setCurrentlyEditingLabelId(label.id);
        editSheetProps.dispatchOpenSheet();
    };

    return (
        <>
            <ToolbarTool name={name} isOpen={isSheetOpen} onClick={dispatchOpenSheet}>
                <Icon gridSize={gridSize} name="toolbar-label" />
            </ToolbarTool>
            <SheetContainer className="LabelsToolbarSheet" {...sheetProps} defaultSnapPoint={0.5}>
                <Overlay />
                <Sheet>
                    <Header>
                        <Heading>{name}</Heading>
                        <CloseButton />
                    </Header>
                    <Content>
                        <LabelsSheetContent
                            {...props}
                            labelMap={labelMap}
                            handleClickEdit={handleClickEdit}
                            currentUserId={currentUserId}
                        />
                    </Content>
                </Sheet>
            </SheetContainer>
            {currentlyEditingLabel && (
                <>
                    <LabelEditSheet
                        colorSheetProps={colorSheetProps}
                        label={currentlyEditingLabel}
                        sheetProps={editSheetProps}
                        resetEditingId={() => setCurrentlyEditingLabelId(null)}
                    />
                    <LabelColorSheet label={currentlyEditingLabel} sheetProps={colorSheetProps} />
                </>
            )}
        </>
    );
};

export default MobileLabelsTool;
